import React, { cloneElement, useRef, useState } from "react";
import { useClickOutside } from "./hooks/useClickOutside";
import { useDropdownPosition } from "./hooks/useDropdownPosition";
import { usePopper } from "react-popper";
import { bottom, top, left, right } from "@popperjs/core";

function isReactElement(value) {
  return React.isValidElement(value);
}

const SelectBox = ({
  children,
  selectedValue,
  disabled,
  onChange = () => {},
  multiple = false,
  xPlacement = "bottomLeft",
}) => {
  const wrapperRef = useRef(null);
  const [targetElement, setTargetElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [open, setOpen] = useState(false);
  const [dropdownPosition, calculateDropdownPosition] = useDropdownPosition();

  const toggleOpen = () =>
    setOpen((prev) => {
      const next = !prev;
      if (next) calculateDropdownPosition(wrapperRef);
      return next;
    });
  const close = () => setOpen(false);
  const openDropdown = () => setOpen(true);

  useClickOutside(wrapperRef, () => {
    setOpen(false);
  });

  const placements = {
    bottom: bottom,
    bottomLeft: "bottom-start",
    bottomRight: "bottom-end",
    top: top,
    topLeft: "top-start",
    topRight: "top-end",
    right: right,
    left: left,
    "": bottom,
  };

  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: placements[xPlacement || ""],
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 0],
        },
      },
    ],
  });

  const sharedProps = {
    open,
    toggleOpen,
    close,
    openDropdown,
    wrapperRef,
    dropdownPosition,
    onChange,
    disabled,
    multiple,
    targetElement,
    setTargetElement,
    popperElement,
    setPopperElement,
    styles,
    selected: selectedValue,
  };

  const renderChildrenWithProps = (children_props) => {
    if (typeof children_props === "function") {
      const result = children_props(sharedProps);
      return renderChildrenWithProps(result);
    }

    return React.Children.map(children_props, (child) => {
      if (!isReactElement(child)) return child;

      const newProps = typeof child.type !== "string" ? sharedProps : {};

      return cloneElement(
        child,
        newProps,
        child.props?.children
          ? renderChildrenWithProps(child.props.children)
          : undefined
      );
    });
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      {renderChildrenWithProps(children)}
    </div>
  );
};

export default SelectBox;
